import React, { useState, useEffect, useRef } from 'react';
import { Layout, Space, Typography, Button, Input, App, Modal, Tooltip } from 'antd';
import { CalculatorOutlined, SaveOutlined, ShareAltOutlined, DownloadOutlined, LoadingOutlined, HistoryOutlined, LogoutOutlined, BookOutlined, TeamOutlined, LockOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../../features/language-switch/LanguageSwitcher';
import type { CalculatorData } from '../calculator/Calculator';
import type { SalesPlanData } from '../forecast/SalesPlan';
import type { BudgetCategory } from '../budget/Budget';
import {
  createShareLink,
  exportXlsx,
  logRequestFailure,
  saveResult,
  transportErrorOf,
} from '../../shared/api/client';
import { useAuthStore } from '../../shared/session/authStore';
import { useAppColors } from '../../shared/theme';
import { buildBackendPayload } from '../../shared/lib';
import History from '../history/History';
import DocumentationModal from './DocumentationModal';
import VersionChangelog from './VersionChangelog';
import ChangePasswordModal from './ChangePasswordModal';
import AdminUsersModal from '../../features/admin-users/AdminUsersModal';

// Задержка, на которую наблюдатель секций замолкает после клика по навигации —
// столько идёт плавный скролл браузера.
const SCROLL_UNLOCK_DELAY_MS = 800;

const NAV_ITEM_KEYS = [
  { id: 'calculator', titleKey: 'idea_calc.header.nav_calculator' },
  { id: 'competitor-analysis', titleKey: 'idea_calc.header.nav_competitors' },
  { id: 'sales-plan', titleKey: 'idea_calc.header.nav_sales_plan' },
  { id: 'forecast', titleKey: 'idea_calc.header.nav_forecast' },
  { id: 'summary', titleKey: 'idea_calc.header.nav_summary' },
  { id: 'notes', titleKey: 'idea_calc.header.nav_notes' },
];

// Nav with IntersectionObserver for smooth scroll tracking (no jitter)
const HeaderNav: React.FC = () => {
  const { t } = useTranslation();
  const [activeId, setActiveId] = useState('calculator');
  const isClickScrolling = useRef(false);
  const unlockTimerRef = useRef<number | null>(null);

  // Track sections with IntersectionObserver
  useEffect(() => {
    const observers: IntersectionObserver[] = [];
    const visibleSections = new Map<string, number>();

    NAV_ITEM_KEYS.forEach((item) => {
      const element = document.getElementById(item.id);
      if (!element) return;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              visibleSections.set(item.id, entry.intersectionRatio);
            } else {
              visibleSections.delete(item.id);
            }

            // Don't update during click-triggered scroll
            if (isClickScrolling.current) return;

            // Find the most visible section
            let maxRatio = 0;
            let mostVisible = 'calculator';
            visibleSections.forEach((ratio, id) => {
              if (ratio > maxRatio) {
                maxRatio = ratio;
                mostVisible = id;
              }
            });

            if (visibleSections.size > 0) {
              setActiveId(mostVisible);
            }
          });
        },
        { threshold: [0.1, 0.5], rootMargin: '-80px 0px -50% 0px' }
      );

      observer.observe(element);
      observers.push(observer);
    });

    return () => observers.forEach((o) => o.disconnect());
  }, []);

  // Таймер разблокировки переживал бы размонтирование и срабатывал на мёртвом ref:
  // в MF-режиме модуль монтируется и размонтируется в одной вкладке многократно.
  useEffect(() => () => {
    if (unlockTimerRef.current !== null) window.clearTimeout(unlockTimerRef.current);
  }, []);

  const handleClick = (id: string) => {
    isClickScrolling.current = true;
    setActiveId(id);

    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    // Allow observer updates again after scroll animation.
    // Предыдущий таймер снимается: иначе их накопится несколько, и очистка при
    // размонтировании знала бы только о последнем.
    if (unlockTimerRef.current !== null) window.clearTimeout(unlockTimerRef.current);
    unlockTimerRef.current = window.setTimeout(() => {
      unlockTimerRef.current = null;
      isClickScrolling.current = false;
    }, SCROLL_UNLOCK_DELAY_MS);
  };

  return (
    <nav className="header-nav" style={{ display: 'flex', gap: 8, flex: 1, justifyContent: 'center' }}>
      {NAV_ITEM_KEYS.map((item) => (
        <button
          key={item.id}
          onClick={() => handleClick(item.id)}
          className={`header-nav-link ${activeId === item.id ? 'active' : ''}`}
        >
          {t(item.titleKey)}
        </button>
      ))}
    </nav>
  );
};

export interface HeaderProps {
  calculatorData?: CalculatorData;
  salesPlanData?: SalesPlanData;
  budgetData?: BudgetCategory[];
  notesData?: string;
  onLoadResult?: (fileName: string) => void;
  onLoadShared?: (data: Record<string, unknown>) => void;
  loadedFileName?: string;
}

const Header: React.FC<HeaderProps> = ({ calculatorData, salesPlanData, budgetData, notesData, onLoadResult, onLoadShared, loadedFileName }) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const c = useAppColors();
  const { isAuthenticated, isAdmin, mode } = useAuthStore();
  const [calcName, setCalcName] = useState('');
  const [saving, setSaving] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [docsOpen, setDocsOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [usersOpen, setUsersOpen] = useState(false);


  // When a saved result is loaded, populate name fields
  React.useEffect(() => {
    if (loadedFileName) {
      setCalcName(loadedFileName);
    }
  }, [loadedFileName]);

  const handleSave = async () => {
    const name = calcName.trim();
    if (!name) {
      message.warning(t('idea_calc.header.msg_enter_name'));
      return;
    }
    setSaving(true);
    try {
      const payload = buildBackendPayload(calculatorData, salesPlanData, budgetData, notesData);
      const res = await saveResult(name, payload);
      if (res.success) {
        message.success(t('idea_calc.header.msg_saved'));
      } else {
        message.error(res.message || t('idea_calc.header.msg_save_error'));
      }
    } catch (e: unknown) {
      logRequestFailure('Save error:', e);
      message.error(t('idea_calc.header.msg_save_failed'));
    } finally {
      setSaving(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const payload = buildBackendPayload(calculatorData, salesPlanData, budgetData, notesData);
      const blob = await exportXlsx(payload);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `calculator_export_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      message.success(t('idea_calc.header.msg_file_downloaded'));
    } catch (e: unknown) {
      // responseType: 'blob' → тело ошибки тоже приходит блобом, и `detail`
      // читается только текстом. Само тело живёт в транспортной ошибке под ApiError.
      const body = transportErrorOf(e)?.response?.data;
      let detail = '';
      if (body instanceof Blob) {
        try {
          const text = await body.text();
          const json = JSON.parse(text);
          detail = json.detail || text;
        } catch { detail = t('idea_calc.common.error'); }
      } else {
        detail = (body as { detail?: string } | undefined)?.detail
          || (e instanceof Error ? e.message : '');
      }
      logRequestFailure('Export error:', e, detail);
      message.error(t('idea_calc.header.msg_export_error', { detail }));
    } finally {
      setExporting(false);
    }
  };

  const handleShare = async () => {
    const name = calcName.trim();
    if (!name) {
      message.warning(t('idea_calc.header.msg_save_first'));
      return;
    }
    setSharing(true);
    try {
      // Always save current data before sharing to ensure share link has latest data
      const payload = buildBackendPayload(calculatorData, salesPlanData, budgetData, notesData);
      const saveRes = await saveResult(name, payload);
      if (!saveRes.success) {
        message.error(t('idea_calc.header.msg_save_before_share_failed'));
        return;
      }
      const res = await createShareLink(name);
      const shareId = res.share_id;
      const rawUrl = res.url || `/s/${shareId}`;
      let shareUrl: string;
      if (rawUrl.startsWith('http')) {
        shareUrl = rawUrl;
      } else {
        const base = (import.meta.env.VITE_BASE_PATH || '/').replace(/\/+$/, '');
        shareUrl = `${window.location.origin}${base}${rawUrl}`;
      }

      try {
        await navigator.clipboard.writeText(shareUrl);
        message.success(t('idea_calc.header.msg_link_copied'));
      } catch {
        // Clipboard недоступен (нет разрешения, небезопасный контекст) — показываем
        // ссылку, чтобы пользователь скопировал её руками.
        Modal.info({ title: t('idea_calc.header.share_link_label'), content: shareUrl });
      }
    } catch (e: unknown) {
      logRequestFailure('Share error:', e);
      message.error(t('idea_calc.header.msg_share_error'));
    } finally {
      setSharing(false);
    }
  };

  return (
    <Layout.Header
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 100,
        background: c.brandBar,
        display: 'flex',
        alignItems: 'center',
        height: 64,
        padding: '0 24px',
        gap: 16,
      }}
    >
      <Space size={12} align="center" style={{ flexShrink: 0 }}>
        <CalculatorOutlined style={{ fontSize: 28, color: c.info }} />
        <Typography.Title level={4} style={{ color: c.onFilled, margin: 0, whiteSpace: 'nowrap' }}>
          {t('idea_calc.header.logo_title')}
        </Typography.Title>
      </Space>
      <HeaderNav />
      <Space size={8} style={{ flexShrink: 0 }}>
        {/* Хедер рендерится только в standalone (в embed хром даёт платформа),
            поэтому переключатель языка здесь безусловен. */}
        <LanguageSwitcher />
        <Input
          size="small"
          placeholder={t('idea_calc.header.name_placeholder')}
          value={calcName}
          onChange={(e) => {
            const scrollY = window.scrollY;
            setCalcName(e.target.value);
            requestAnimationFrame(() => {
              window.scrollTo({ top: scrollY, behavior: 'instant' });
            });
          }}
          style={{ width: 140 }}
          onPressEnter={handleSave}
        />
        <Button
          size="small"
          type="primary"
          icon={saving ? <LoadingOutlined /> : <SaveOutlined />}
          onClick={handleSave}
          disabled={saving}
        >
          {t('idea_calc.header.save_button')}
        </Button>
        <Tooltip title={t('idea_calc.header.export_tooltip')}>
          <Button
            size="small"
            icon={exporting ? <LoadingOutlined /> : <DownloadOutlined />}
            onClick={handleExport}
            disabled={exporting}
            style={{ color: c.info, borderColor: c.info }}
          />
        </Tooltip>
        <Tooltip title={t('idea_calc.header.share_tooltip')}>
          <Button
            size="small"
            icon={sharing ? <LoadingOutlined /> : <ShareAltOutlined />}
            onClick={handleShare}
            disabled={sharing}
            style={{ color: c.info, borderColor: c.info }}
          />
        </Tooltip>
        <VersionChangelog />
        <Tooltip title={t('idea_calc.header.docs_tooltip')}>
          <Button
            size="small"
            icon={<BookOutlined />}
            onClick={() => setDocsOpen(true)}
            style={{ color: c.info, borderColor: c.info }}
          />
        </Tooltip>
        {isAuthenticated && (
          <Tooltip title={t('idea_calc.header.history_tooltip')}>
            <Button
              size="small"
              icon={<HistoryOutlined />}
              onClick={() => setHistoryOpen(true)}
              style={{ color: c.info, borderColor: c.info }}
            />
          </Tooltip>
        )}
        {isAuthenticated && isAdmin && (
          <Tooltip title={t('idea_calc.header.admin_users_tooltip')}>
            <Button
              size="small"
              icon={<TeamOutlined />}
              onClick={() => setUsersOpen(true)}
              style={{ color: c.info, borderColor: c.info }}
            />
          </Tooltip>
        )}
        {isAuthenticated && mode === 'dashboard' && (
          <Tooltip title={t('idea_calc.header.change_password_tooltip')}>
            <Button
              size="small"
              icon={<LockOutlined />}
              onClick={() => setPasswordOpen(true)}
              style={{ color: c.info, borderColor: c.info }}
            />
          </Tooltip>
        )}
        {mode === 'dashboard' && (
          <Tooltip title={t('idea_calc.header.logout_tooltip')}>
            <Button
              size="small"
              icon={<LogoutOutlined />}
              onClick={() => useAuthStore.getState().logout()}
              style={{ color: c.error, borderColor: c.error }}
            />
          </Tooltip>
        )}
      </Space>
      {isAuthenticated && onLoadResult && onLoadShared && (
        <History open={historyOpen} onClose={() => setHistoryOpen(false)} onLoadResult={onLoadResult} onLoadShared={onLoadShared} />
      )}
      <DocumentationModal open={docsOpen} onClose={() => setDocsOpen(false)} />
      <ChangePasswordModal open={passwordOpen} onClose={() => setPasswordOpen(false)} />
      {isAuthenticated && isAdmin && (
        <AdminUsersModal open={usersOpen} onClose={() => setUsersOpen(false)} />
      )}
    </Layout.Header>
  );
};

export default Header;
